/**
 * Multi-Study Flow Reducer
 *
 * Manages state transitions for the multi-study prescreening flow.
 * Implements a clear state machine with type-safe transitions.
 *
 * Flow: common → zipcode → study-specific → complete
 */

import { FlowState, FlowAction } from "../types";

/**
 * Initial state - starts with common questions
 */
export const initialFlowState: FlowState = {
  stage: "common",
  data: {},
};

/**
 * Reducer for flow state transitions
 */
export function flowReducer(state: FlowState, action: FlowAction): FlowState {
  switch (action.type) {
    case "COMPLETE_COMMON": {
      // Transition: common → zipcode
      return {
        stage: "zipcode",
        data: {
          commonAnswers: action.payload.commonAnswers,
        },
      };
    }

    case "COMPLETE_ZIPCODE": {
      if (state.stage !== "zipcode") {
        console.error(
          `[flowReducer] Cannot complete zipcode from stage: ${state.stage}`
        );
        return state;
      }

      // Check if any studies are location-eligible
      if (action.payload.eligibleStudies.length === 0) {
        // No location match - skip to complete with failure
        return {
          stage: "complete",
          data: {
            commonAnswers: state.data.commonAnswers,
            zipcode: action.payload.zipcode,
            matchedStudy: null,
            allAnswers: {
              ...state.data.commonAnswers,
              zipcode: action.payload.zipcode,
            },
          },
        };
      }

      // Transition: zipcode → study-specific (start with first study)
      return {
        stage: "study-specific",
        data: {
          commonAnswers: state.data.commonAnswers,
          zipcode: action.payload.zipcode,
          eligibleStudies: action.payload.eligibleStudies,
          currentStudyIndex: 0,
        },
      };
    }

    case "COMPLETE_STUDY_SPECIFIC": {
      if (state.stage !== "study-specific") {
        console.error(
          `[flowReducer] Cannot complete study-specific from stage: ${state.stage}`
        );
        return state;
      }

      const { passed, studyAnswers } = action.payload;
      const {
        commonAnswers,
        zipcode,
        eligibleStudies,
        currentStudyIndex,
      } = state.data;

      if (passed) {
        // SUCCESS! Matched to current study
        const matchedStudy = eligibleStudies[currentStudyIndex];

        return {
          stage: "complete",
          data: {
            commonAnswers,
            zipcode,
            matchedStudy,
            allAnswers: {
              ...commonAnswers,
              zipcode,
              ...studyAnswers,
            },
          },
        };
      }

      // FAILED current study - check if we can cascade to next
      const nextIndex = currentStudyIndex + 1;

      if (nextIndex < eligibleStudies.length) {
        // CASCADE to next study - stay in study-specific stage
        return {
          stage: "study-specific",
          data: {
            commonAnswers,
            zipcode,
            eligibleStudies,
            currentStudyIndex: nextIndex,
          },
        };
      }

      // No more studies to try - complete with failure
      return {
        stage: "complete",
        data: {
          commonAnswers,
          zipcode,
          matchedStudy: null,
          allAnswers: {
            ...commonAnswers,
            zipcode,
            ...studyAnswers,
          },
        },
      };
    }

    case "NO_LOCATION_MATCH": {
      if (state.stage !== "zipcode") {
        console.error(
          `[flowReducer] Cannot handle no location match from stage: ${state.stage}`
        );
        return state;
      }

      // Complete with failure due to location
      return {
        stage: "complete",
        data: {
          commonAnswers: state.data.commonAnswers,
          zipcode: "", // No zipcode available if we didn't get to that stage
          matchedStudy: null,
          allAnswers: state.data.commonAnswers,
        },
      };
    }

    default:
      return state;
  }
}

/**
 * Helper: Get current study from flow state (if in study-specific stage)
 */
export function getCurrentStudy(state: FlowState) {
  if (state.stage === "study-specific") {
    return state.data.eligibleStudies[state.data.currentStudyIndex];
  }
  return null;
}

/**
 * Helper: Check if flow is complete
 */
export function isFlowComplete(state: FlowState): boolean {
  return state.stage === "complete";
}

/**
 * Helper: Get final match result from completed flow
 */
export function getMatchResult(state: FlowState) {
  if (state.stage === "complete") {
    return {
      matched: state.data.matchedStudy !== null,
      study: state.data.matchedStudy,
      allAnswers: state.data.allAnswers,
    };
  }
  return null;
}
